import { lazy, Suspense, useEffect, useState } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import LoadingState from "../../components/feedback/LoadingState";
import { getTicketHistory } from "../../api/tickets";
import ProtectedRoute from "../routes/ProtectedRoute";

const PPMain = lazy(() => import("../ppMain/PPMain"));
const RecoveryPassword = lazy(() => import("../ppMain/RecoveryPassword"));
const SaltexDashboard = lazy(() => import("../../pages/SaltexDashboard"));
const SaltexUsers = lazy(() => import("../../pages/SaltexUsers"));
const SaltexTickets = lazy(() => import("../../pages/SaltexTickets"));
const SaltexTask = lazy(() => import("../../pages/SaltexTasks"));
const SaltexTaskDetails = lazy(() => import("../../pages/SaltexTasksDetails"));
const SaltexTicketsDetails = lazy(() => import("../../pages/SaltexTicketsDetails"));
const SaltexOrganizations = lazy(() => import("../../pages/SaltexOrganizations"));
const SaltexClosedTask = lazy(() => import("../../pages/SaltextClosedTask"));
const SaltexNewUsers = lazy(() => import("../../pages/SaltexNewUsers"));
const SaltexNewUsersActivation = lazy(() => import("../../pages/SaltexNewUsersActivation"));
const SaltexNewPasswd = lazy(() => import("../../pages/SaltexNewPasswd"));
const SaltextEditOrganization = lazy(() => import("../../pages/SaltexEditOrganization"));
const SaltexEditUser = lazy(() => import("../../pages/SaltexEditUser"));
const SaltexProfile = lazy(() => import("../../pages/SaltexProfile"));
const SaltexReports = lazy(() => import("../../pages/SaltexReports"));
const SaltexReportsTasks = lazy(() => import("../../pages/SaltexReportsTasks"));
const SaltexBillingTicket = lazy(() => import("../../pages/SaltexBillingTickets"));
const SaltexSalesDashboard = lazy(() => import("../../pages/SaltexSalesDashboard"));
const SaltexBillingPDF = lazy(() => import("../../pages/SaltexBillingPDF"));

const App = () => {
  const [openTickets, setOpenTickets] = useState(0);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) return;

    const today = new Date();
    const last30Days = new Date(today);
    last30Days.setDate(today.getDate() - 30);

    getTicketHistory({
      role: user.role,
      id: user._id,
      startDate: last30Days,
      endDate: today,
    })
      .then((response) => {
        const total = (response.data.open || []).reduce(
          (sum, register) => sum + register.count,
          0
        );
        setOpenTickets(total);
      })
      .catch(() => setOpenTickets(0));
  }, []);

  const router = createBrowserRouter([
    { path: "/NewUser", element: <SaltexNewUsers /> },
    { path: "/NewUserActivation/*", element: <SaltexNewUsersActivation /> },
    { path: "/", element: <PPMain /> },
    { path: "/recovery-password", element: <RecoveryPassword /> },
    {
      path: "dashboard",
      element: <ProtectedRoute><SaltexDashboard openTickets={openTickets} /></ProtectedRoute>,
    },
    {
      path: "/tickets/",
      element: <ProtectedRoute><SaltexTickets openTickets={openTickets} /></ProtectedRoute>,
    },
    {
      path: "/users",
      element: <ProtectedRoute><SaltexUsers openTickets={openTickets} /></ProtectedRoute>,
    },
    {
      path: "/user/edit",
      element: <ProtectedRoute><SaltexEditUser openTickets={openTickets} /></ProtectedRoute>,
    },
    {
      path: "/tasks",
      element: <ProtectedRoute><SaltexTask openTickets={openTickets} /></ProtectedRoute>,
    },
    {
      path: "/closedTask/:id/:ticketId",
      element: <ProtectedRoute><SaltexClosedTask openTickets={openTickets} /></ProtectedRoute>,
    },
    {
      path: "/tasksDetails/*",
      element: <ProtectedRoute><SaltexTaskDetails openTickets={openTickets} /></ProtectedRoute>,
    },
    {
      path: "/billingPDF/:estimateNumber",
      element: <ProtectedRoute><SaltexBillingPDF openTickets={openTickets} /></ProtectedRoute>,
    },
    {
      path: "/billingTicket/*",
      element: <ProtectedRoute><SaltexBillingTicket openTickets={openTickets} /></ProtectedRoute>,
    },
    {
      path: "/salesDashboard/*",
      element: <ProtectedRoute><SaltexSalesDashboard openTickets={openTickets} /></ProtectedRoute>,
    },
    {
      path: "/ticketsDetails/*",
      element: <ProtectedRoute><SaltexTicketsDetails openTickets={openTickets} /></ProtectedRoute>,
    },
    {
      path: "/reports",
      element: <ProtectedRoute><SaltexReports openTickets={openTickets} /></ProtectedRoute>,
    },
    {
      path: "/reports-tasks",
      element: <ProtectedRoute><SaltexReportsTasks openTickets={openTickets} /></ProtectedRoute>,
    },
    {
      path: "/Organizations",
      element: <ProtectedRoute><SaltexOrganizations openTickets={openTickets} /></ProtectedRoute>,
    },
    {
      path: "/Organization/edit",
      element: <ProtectedRoute><SaltextEditOrganization openTickets={openTickets} /></ProtectedRoute>,
    },
    {
      path: "/ResetPassword",
      element: <ProtectedRoute><SaltexNewPasswd /></ProtectedRoute>,
    },
    {
      path: "/Profile",
      element: <ProtectedRoute><SaltexProfile openTickets={openTickets} /></ProtectedRoute>,
    },
    {
      path: "/profile-update",
      element: <ProtectedRoute><Navigate to="/Profile" replace /></ProtectedRoute>,
    },
  ]);

  return (
    <Suspense fallback={<LoadingState message="Loading application…" minHeight="100vh" />}>
      <RouterProvider router={router} />
    </Suspense>
  );
};

export default App;
